import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "../auth/apiClient";
import { useAuth } from "../auth/AuthContext";
import type { Menu, UserMenuOverride, Role } from "./types";

export type RbacContextValue = {
  // Optional app scoping; set null if not used
  appId: string | null;
  setAppId: (id: string | null) => void;

  // Current user id used to evaluate effective menus
  currentUserId: string | null;
  setCurrentUserId: (id: string | null) => void;

  effectiveMenus: Menu[];
  roles: Role[];
  isAdmin: boolean;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  canAccess: (keyOrPath: string) => boolean;
};

const RbacContext = createContext<RbacContextValue | undefined>(undefined);

export function RbacProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [appId, setAppId] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [effectiveMenus, setEffectiveMenus] = useState<Menu[]>([]);
  const [userRoles, setUserRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true); // Start with loading=true
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Standard flow: resolve currentUserId from /rbac/users by email, then fetch permissions
  useEffect(() => {
    async function initializeRbac() {
      // Wait for auth to finish
      if (authLoading) return;

      // No user = no permissions needed
      if (!user?.email) {
        setCurrentUserId(null);
        setEffectiveMenus([]);
        setUserRoles([]);
        setIsLoading(false);
        setIsInitialized(true);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Step 1: Resolve user ID
        const { data: users } = await api.get<Array<{ id: string; email: string }>>('/rbac/users', { params: { skip: 0, limit: 1000 } });
        const match = users.find(u => u.email?.toLowerCase() === user.email.toLowerCase());
        
        if (!match) {
          console.warn(`User with email ${user.email} not found in /rbac/users. Permissions will be empty.`);
          setCurrentUserId(null);
          setEffectiveMenus([]);
          setUserRoles([]);
          setIsLoading(false);
          setIsInitialized(true);
          return;
        }

        const uid = match.id;
        setCurrentUserId(uid);

        // Step 2: Fetch permissions (roles + menus + overrides)
        const { data: roles } = await api.get<Role[]>(`/rbac/users/${uid}/roles`);
        const filteredRoles = appId ? roles.filter(r => (r.app_id ?? null) === appId || r.app_id == null) : roles;
        setUserRoles(filteredRoles);

        // Fetch menus from all roles in parallel
        const menusArrays = await Promise.all(
          filteredRoles.map(r => api.get<Menu[]>(`/rbac/roles/${r.id}/menus`).then(res => res.data))
        );
        
        const menusSet = new Map<string, Menu>();
        menusArrays.flat().forEach(m => {
          if (!appId || m.app_id === appId) {
            menusSet.set(m.id, m);
          }
        });

        // Fetch overrides
        const { data: overrides } = await api.get<UserMenuOverride[]>(`/rbac/users/${uid}/overrides`);
        const filteredOverrides = appId ? overrides.filter(o => o.menu?.app_id === appId || !o.menu) : overrides;

        for (const ov of filteredOverrides) {
          if (!ov.menu) continue;
          if (ov.has_access) {
            menusSet.set(ov.menu.id, ov.menu);
          } else {
            menusSet.delete(ov.menu.id);
          }
        }

        setEffectiveMenus(Array.from(menusSet.values()));
      } catch (e: any) {
        console.error('Failed to load RBAC permissions:', e);
        setError(e?.response?.data?.detail || e?.message || "Failed to load permissions");
        setEffectiveMenus([]);
        setUserRoles([]);
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
      }
    }

    void initializeRbac();
  }, [user?.email, authLoading, appId]);

  // Refetch permissions manually (for admin operations that change permissions)
  const refetch = useCallback(async () => {
    if (!currentUserId) {
      setEffectiveMenus([]);
      setUserRoles([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const { data: roles } = await api.get<Role[]>(`/rbac/users/${currentUserId}/roles`);
      const filteredRoles = appId ? roles.filter(r => (r.app_id ?? null) === appId || r.app_id == null) : roles;
      setUserRoles(filteredRoles);

      const menusArrays = await Promise.all(
        filteredRoles.map(r => api.get<Menu[]>(`/rbac/roles/${r.id}/menus`).then(res => res.data))
      );
      
      const menusSet = new Map<string, Menu>();
      menusArrays.flat().forEach(m => {
        if (!appId || m.app_id === appId) {
          menusSet.set(m.id, m);
        }
      });

      const { data: overrides } = await api.get<UserMenuOverride[]>(`/rbac/users/${currentUserId}/overrides`);
      const filteredOverrides = appId ? overrides.filter(o => o.menu?.app_id === appId || !o.menu) : overrides;

      for (const ov of filteredOverrides) {
        if (!ov.menu) continue;
        if (ov.has_access) {
          menusSet.set(ov.menu.id, ov.menu);
        } else {
          menusSet.delete(ov.menu.id);
        }
      }

      setEffectiveMenus(Array.from(menusSet.values()));
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load permissions");
      setEffectiveMenus([]);
      setUserRoles([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentUserId, appId]);

  const canAccess = useCallback((keyOrPath: string) => {
    return effectiveMenus.some(m => m.menu_key === keyOrPath || m.path === keyOrPath);
  }, [effectiveMenus]);

  // Evaluasi apakah user memiliki role 'Admin' dari database db_system
  const isAdmin = useMemo(() => {
    return userRoles.some(r => r.role_name?.trim().toLowerCase() === 'admin');
  }, [userRoles]);

  const value = useMemo<RbacContextValue>(() => ({
    appId,
    setAppId,
    currentUserId,
    setCurrentUserId,
    effectiveMenus,
    roles: userRoles,
    isAdmin,
    isLoading,
    error,
    refetch,
    canAccess,
  }), [appId, currentUserId, effectiveMenus, userRoles, isAdmin, isLoading, error, refetch, canAccess]);

  // Show loading screen until RBAC permissions are fully loaded (only on initial load)
  if (!isInitialized) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        background: '#f7fafc'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            fontSize: '24px', 
            marginBottom: '16px',
            color: '#2d3748'
          }}>Loading permissions...</div>
          <div style={{ color: '#718096' }}>Please wait</div>
        </div>
      </div>
    );
  }

  return <RbacContext.Provider value={value}>{children}</RbacContext.Provider>;
}

export function useRbac() {
  const ctx = useContext(RbacContext);
  if (!ctx) throw new Error("useRbac must be used within RbacProvider");
  return ctx;
}
